/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {RateTimeTable} from '@ion/core/src/types/entities';
import Dropdown, {Options} from '../components/menus/Dropdown';
import {SCIDeviceSettingsFormField} from './DeviceEditorUtils';

export type ReportingRateChooserProps = {
  rate?: number;
  readOnly?: boolean;
  onChange?: Function
  name?: string;
  fieldLabel?: string;
  fallbackValue?: boolean;
};

/**
 * utility function to convert time in seconds to string value
 * used in dropdown menus.
 */
const convertTimeToValue = (time: number, fallback = true): string => {
  if (time > 0) {
    const minutes = time / 60;
    if (minutes < 60) {
      const unit = minutes === 1 ? 'minute' : 'minutes';
      return `${minutes} ${translate(`common:editors.reportingRateUnits.${unit}`)}`;
    } else {
      const unit = minutes === 60 ? 'hour' : 'hours';
      return `${minutes / 60} ${translate(`common:editors.reportingRateUnits.${unit}`)}`;
    }
  };
  return fallback ? translate('admin-devices:editors.reportingRates.none') : null;
};

/**
 * Generates the reporting rate chooser dropdown labels and values based on
 * the translated reporting rates.
 */
export const getOptions = (name: string): Options[] => {
  const entries  = Object.entries(translate('admin-devices:editors.reportingRates', {returnObjects: true}));
  const mapping = entries.map(([value, label]): Options => ({value, label}));
  // in case of temperature logging, we'd like to limit to 8 hours
  if (name === SCIDeviceSettingsFormField.SensorLoggingPeriod) {
    // filter time table to only show up to 8 hours. (index 10 of time table array is 8 hours).
    const filteredTimeTable = mapping.slice(0, 11);
    const stopReporting = mapping[mapping.length-1];
    return filteredTimeTable.concat(stopReporting);
  };
  // in case of When In Motion and When In Stationary, we'd like to remove option for STOP REPORTING
  if (name === SCIDeviceSettingsFormField.MoveWakeupPeriod ||
    name === SCIDeviceSettingsFormField.WakeupPeriod
  ) {
    const filteredTimeTable = mapping.slice(0, 14);
    return filteredTimeTable;
  }
  // for default cases, time table should be up to 24 hours.
  return mapping;
};

/**
 * @ReportingRateChooser
 *
 * The ReportingRateChooser provides various dropdown menu based on
 * types of reporting rate controls for SCI devices.
 *
 * ```typescript
 *
 *     import {ReportingRateChooser} from '@ion/dls/src/editors/';
 *
 *    <ReportingRateChooser
 *      fieldLabel='When in Motion'
 *      readOnly={loading || isViewMode}
 *      rate={formState[SCIDeviceSettingsFormField.WakeupPeriod]?.value}
 *      name={SCIDeviceSettingsFormField.WakeupPeriod}
 *      onChange={handleChooserChange}
 *       />*

 *
 * ```
 *
 * @param {Function} onChange: Callback prop which will be invoked with dropdown menu changes
 * @param {string} fieldLabel: Overrides the default field label
 * @param {string} name: for the component that gets passed to the root element.
 * @param {boolean} readOnly: When true, the component will be displayed in read-only mode;
 *   that is, disabled. Defaults to false.
 * @param {number} rate: Provides the current value used in the dropDown.
 */
export const ReportingRateChooser = ({
  rate,
  readOnly,
  onChange,
  name,
  fieldLabel,
  fallbackValue = true
}: ReportingRateChooserProps) => {
  // initial loading from the response value
  useEffect(() => {
    if (rate) {
      onChange?.({name, value: rate});
    }
  }, []);

  return (
    <Dropdown required={false}
      displaySelectedLabel
      disabled={readOnly}
      value={convertTimeToValue(rate, fallbackValue)}
      options={getOptions(name)}
      placeholder={translate('admin-devices:editors.reportingRateChooser.placeholder')}
      fieldLabel={fieldLabel}
      onChange={onChange ? function({target}) {
        onChange({
          name    : target.name || name,
          value   : RateTimeTable[target.label],
          changed : true
        });
      } : null} />
  );
};
export default ReportingRateChooser;
